import { useCallback, useEffect, useMemo, useState } from 'react';
import Order from './infoOrderOrder';
import Patient from './infoOrderPatient';
import './infoOrder.scss';
import { useAppDispatch, useAppSelector } from '../../../contexts/hooks';
import TabPanel from 'devextreme-react/tab-panel';
import { formatMessage } from 'devextreme/localization';
import { Attachments, Notes, Results, SerumLibrary, Studies, Tracking, OrdInformedConsents } from './OrderModules';
import { useApiOrders } from '../../../api/api.orders';
import { shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setCurrentActiveTab } from '../../../contexts/slices/orders';

interface LocationState {
  selectedTabId?: number;
}

const Index = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const selectedTabId = (location.state as LocationState)?.selectedTabId ?? 0;
  const [selectedIndex, setSelectedIndex] = useState(selectedTabId);
  const { getLstBacteriologist } = useApiOrders();
  const ColumnsContainerOrderOrders = useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'ColumnsContainerOrderOrders'), shallowEqual)?.Cgn_Resultado;

  const ColumnsContainerPatientOrders = useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'ColumnsContainerPatientOrders'), shallowEqual)?.Cgn_Resultado;

  const ModuleTracking = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'ModuleTracking'), shallowEqual)?.Cgn_Resultado || 'false');
  const ModuleClinicalData = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'ModuleClinicalData'), shallowEqual)?.Cgn_Resultado || 'false');
  const ModuleSamples = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'ModuleSamples'), shallowEqual)?.Cgn_Resultado || 'false');
  const ModuleAttachment = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'ModuleAttachment'), shallowEqual)?.Cgn_Resultado || 'false');
  const ModuleInformedConsents = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.find((x) => x.Cgn_Nombre === 'ModuleInformedConsents'), shallowEqual)?.Cgn_Resultado || 'false');

  const Prm_Res = useAppSelector((x) => x.userInfo.Permissions?.Prm_Res);
  const Prm_Ord = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord);
  const Prm_Ord_NotasR = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_NotasR);
  const Prm_Ord_Attachments = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_Attachments);
  const Prm_Ord_Tracking_R = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_Tracking_R);
  const Prm_Ord_Anamnesis_R = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_Anamnesis_R);
  const Prm_Seroteca_R = useAppSelector((x) => x.userInfo.Permissions?.Prm_Seroteca_R);
  const Prm_Ord_Consentimientos_R = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_Consentimientos_R);
  const notes = useAppSelector((x) => x.globalOrders.CurrentOrder.Notes);
  const attachments = useAppSelector((x) => x.globalOrders.CurrentOrder.Attachments);
  const lstAnalitosResultTipoListas = useAppSelector((x) => x.configuration.LstAnalitosResulTipoListas);

  const tabs = useMemo(
    () =>
      [
        { id: 1, title: 'OrdStudies', visible: Prm_Ord },
        { id: 2, title: 'OrdResults', visible: Prm_Res },
        { id: 3, title: 'OrdNotes', visible: Prm_Ord_NotasR },
        { id: 4, title: 'OrdAttachments', visible: Prm_Ord_Attachments && ModuleAttachment },
        { id: 5, title: 'OrdTracking', visible: ModuleTracking && Prm_Ord_Tracking_R },
        { id: 6, title: 'OrdSerumLibrary', visible: Prm_Seroteca_R && ModuleSamples },
        { id: 7, title: 'OrdClinicalData', visible: ModuleClinicalData && Prm_Ord_Anamnesis_R },
        { id: 8, title: 'OrdInformedConsents', visible: ModuleInformedConsents && Prm_Ord_Consentimientos_R },
      ].filter((x) => x.visible),
    [Prm_Ord, Prm_Res, Prm_Ord_NotasR, Prm_Ord_Attachments, Prm_Ord_Tracking_R, ModuleTracking, ModuleClinicalData, Prm_Ord_Anamnesis_R, Prm_Seroteca_R]
  );

  const tabComponents = useMemo(() => {
    return {
      1: <Studies />,
      2: <Results />,
      3: <Notes />,
      4: <Attachments />,
      5: <Tracking />,
      6: <SerumLibrary />,      
      8: <OrdInformedConsents />,
    };
  }, []);

  const renderItemP = useCallback(
    (props: any) => {
      return tabComponents[props.data.id];
    },
    [tabComponents]
  );

  const onSelectionChanged = (args) => {
    if (args.name === 'selectedIndex') {
      const newIndex = args.value;
      setSelectedIndex(newIndex);

      // Actualizar la tab activa en Redux para que los componentes hijos sepan cuál está visible
      const tabId = tabs[newIndex]?.id;
      if (tabId) {
        dispatch(setCurrentActiveTab(tabId));
      }
    }
  };

  useEffect(() => {
    if (lstAnalitosResultTipoListas.length === 0) {
      getLstBacteriologist();
    }

    // Inicializar currentActiveTab en Redux con la tab inicial
    const initialTabId = tabs[selectedIndex]?.id;
    if (initialTabId) {
      dispatch(setCurrentActiveTab(initialTabId));
    }
  }, []);

  const itemTitleRender = (item) => {
    let counter = 0;
    let showBadge = false;
    switch (item.id) {
      case 1:
      case 2:
      case 5:
      case 6:
      case 8:
      default:
        break;
      case 3:
        counter = notes?.length ?? 0;
        showBadge = counter > 0;
        break;
      case 4:
        counter = attachments?.length ?? 0;
        showBadge = counter > 0;
        break;
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span>{formatMessage(item.title)}</span>
        {showBadge && <span className='badge-tab'>{counter}</span>}
      </div>
    );
  };

  return (
    <div>
      <div className='container-forms-ord'>
        <div className={`colOrd-${ColumnsContainerOrderOrders || 6}`}>
          <Order />
        </div>
        <div className={`colOrd-${ColumnsContainerPatientOrders || 6}`}>
          <Patient />
        </div>
      </div>
      <div className='dx-card details-card tab-panel-orders'>
        <TabPanel dataSource={tabs} deferRendering={true} activeStateEnabled={true} swipeEnabled={false} selectedIndex={selectedIndex} itemTitleRender={itemTitleRender} onOptionChanged={onSelectionChanged} animationEnabled={false} loop={false} itemComponent={renderItemP} />
      </div>
    </div>
  );
};

export default Index;
